/**
 * PayrollArrears.js
 *
 * Added vs user's file:
 *  1. Edit button (pencil) in Actions column — pending records only
 *  2. editingId state — null=Add mode, number=Edit mode
 *  3. openEdit(a) — pre-fills form, locks employee dropdown
 *  4. Modal title changes for edit vs add mode
 *  5. save() calls PUT /payroll/arrears/:id for edit, POST /calculate for add
 *  6. UI Validations — isInvalid red borders on all required fields + ₹ InputGroup prefix
 *  7. Effective date: red border when missing (tried to save)
 *  8. Employee: red border when missing (tried to save)
 *  9. Year: red border when out of range
 * 10. Notes: maxLength 500 + character counter
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Table, Badge, Button, Row, Col,
    Form, Modal, Spinner, Alert, InputGroup,
} from 'react-bootstrap';
import { Calculator, CheckCircle, XCircle, Plus, RefreshCw, Edit2 } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';

const MONTH_NAMES = ['','January','February','March','April','May','June',
    'July','August','September','October','November','December'];

const fmt = n => '₹' + parseFloat(n || 0).toLocaleString('en-IN',
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtYearly = n => {
    const v = parseFloat(n || 0) * 12;
    return '₹' + v.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const StatusBadge = ({ status }) => {
    const cfg = {
        pending:  { bg: 'warning', text: 'dark',  label: 'Pending'  },
        approved: { bg: 'success', text: 'white', label: 'Approved' },
        rejected: { bg: 'danger',  text: 'white', label: 'Rejected' },
        paid:     { bg: 'info',    text: 'white', label: 'Paid'     },
    };
    const c = cfg[status] || { bg: 'secondary', text: 'white', label: status };
    return <Badge bg={c.bg} text={c.text} style={{ fontSize: 10 }}>{c.label}</Badge>;
};

const EMPTY_FORM = {
    employee_id:    '',
    old_salary:     '',
    new_salary:     '',
    effective_date: '',
    month:          new Date().getMonth() + 1,
    year:           new Date().getFullYear(),
    notes:          '',
};

const CY = new Date().getFullYear();

// ════════════════════════════════════════════════════════════════════════════
const PayrollArrears = () => {
    const [arrears,      setArrears]      = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [showForm,     setShowForm]     = useState(false);
    const [employees,    setEmployees]    = useState([]);
    const [calcResult,   setCalcResult]   = useState(null);
    const [saving,       setSaving]       = useState(false);
    const [filterStatus, setFilterStatus] = useState('');
    const [form,         setForm]         = useState(EMPTY_FORM);
    // ── Edit mode state ────────────────────────────────────────────────────
    // null = Add mode, number = editing that arrears id
    const [editingId, setEditingId] = useState(null);
    // ── Validation "touched" state ─────────────────────────────────────────
    // Only show red borders after user attempts to save
    const [attempted, setAttempted] = useState(false);

    const setF = (k, v) => {
        setForm(p => ({ ...p, [k]: v }));
        setCalcResult(null);
    };

    const load = useCallback(() => {
        setLoading(true);
        const params = {};
        if (filterStatus) params.status = filterStatus;
        axiosInstance.get('/payroll/arrears', { params })
            .then(r => setArrears(r.data.data || []))
            .catch(err => {
                const msg = err.response?.data?.message || '';
                if (err.response?.status === 500 || msg.toLowerCase().includes('table')) {
                    toast.error('payroll_arrears table may be missing — run migrations');
                } else if (err.response?.status !== 404) {
                    toast.error('Failed to load arrears');
                }
                setArrears([]);
            })
            .finally(() => setLoading(false));
    }, [filterStatus]);

    useEffect(() => {
        axiosInstance.get('/employees')
            .then(r => {
                const data = r.data.data;
                if (Array.isArray(data))                        setEmployees(data);
                else if (data && Array.isArray(data.employees)) setEmployees(data.employees);
                else                                            setEmployees([]);
            })
            .catch(() => {
                axiosInstance.get('/users')
                    .then(r => {
                        const data = r.data.data;
                        if (Array.isArray(data))                        setEmployees(data);
                        else if (data && Array.isArray(data.employees)) setEmployees(data.employees);
                        else                                            setEmployees([]);
                    })
                    .catch(() => setEmployees([]));
            });
    }, []);

    useEffect(() => { load(); }, [load]);

    // ── Open Add modal ─────────────────────────────────────────────────────
    const openAdd = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setCalcResult(null);
        setAttempted(false);
        setShowForm(true);
    };

    // ── Open Edit modal — pre-fill from existing record ────────────────────
    const openEdit = (a) => {
        setEditingId(a.id);
        setForm({
            employee_id:    String(a.employee_id || ''),
            old_salary:     String(a.old_salary || ''),
            new_salary:     String(a.new_salary || ''),
            effective_date: a.effective_date?.slice(0, 10) || '',
            month:          parseInt(a.month) || new Date().getMonth() + 1,
            year:           parseInt(a.year)  || new Date().getFullYear(),
            notes:          a.notes || '',
        });
        setCalcResult(null);
        setAttempted(false);
        setShowForm(true);
    };

    const closeModal = () => {
        setShowForm(false);
        setCalcResult(null);
        setEditingId(null);
        setAttempted(false);
    };

    // ── Preview calculation (same formula as backend) ──────────────────────
    const preview = () => {
        const { old_salary, new_salary, effective_date, month, year } = form;
        if (!old_salary || !new_salary || !effective_date || !month ||
            !year || isNaN(parseInt(year))) {
            toast.error('Fill all required fields to preview');
            return;
        }
        if (parseFloat(new_salary) <= parseFloat(old_salary)) {
            toast.error('New salary must be higher than old salary');
            return;
        }
        const m           = parseInt(month);
        const y           = parseInt(year);
        const daysInMonth = new Date(y, m, 0).getDate();
        const [effYear, effMonth, effDay] = effective_date.split('-').map(Number);
        if (effYear > y || (effYear === y && effMonth > m)) {
            toast.error('Effective date is in the future — no arrears to calculate');
            return;
        }
        let daysAtOld, daysAtNew;
        if (effYear < y || (effYear === y && effMonth < m)) {
            daysAtOld = 0; daysAtNew = daysInMonth;
        } else {
            daysAtOld = effDay - 1;
            daysAtNew = daysInMonth - daysAtOld;
        }
        const dailyOld      = parseFloat(old_salary) / daysInMonth;
        const dailyNew      = parseFloat(new_salary) / daysInMonth;
        const arrearsAmount = parseFloat(((dailyNew - dailyOld) * daysAtNew).toFixed(2));
        setCalcResult({
            days_in_month: daysInMonth, effective_day: effDay,
            days_at_old_salary: daysAtOld, days_at_new_salary: daysAtNew,
            old_daily_rate: dailyOld.toFixed(4), new_daily_rate: dailyNew.toFixed(4),
            old_salary_portion: (dailyOld * daysAtOld).toFixed(2),
            new_salary_portion: (dailyNew * daysAtNew).toFixed(2),
            arrears_amount: arrearsAmount,
        });
    };

    // ── Save (Add or Edit) ─────────────────────────────────────────────────
    const save = async () => {
        setAttempted(true);
        if (!form.employee_id)
            { toast.error('Please select an employee'); return; }
        if (!form.old_salary || parseFloat(form.old_salary) <= 0)
            { toast.error('Old Monthly Salary is required and must be > 0'); return; }
        if (!form.new_salary || parseFloat(form.new_salary) <= 0)
            { toast.error('New Monthly Salary is required and must be > 0'); return; }
        if (parseFloat(form.new_salary) <= parseFloat(form.old_salary))
            { toast.error('New salary must be higher than old salary'); return; }
        if (!form.effective_date)
            { toast.error('Effective date is required'); return; }
        if (!form.year || isNaN(parseInt(form.year)) ||
            parseInt(form.year) < 2020 || parseInt(form.year) > CY + 1)
            { toast.error(`Year must be between 2020 and ${CY + 1}`); return; }
        if (!calcResult)
            { toast.error('Click "Preview Calculation" first'); return; }

        setSaving(true);
        try {
            if (editingId) {
                // EDIT MODE — PUT /payroll/arrears/:id
                await axiosInstance.put(`/payroll/arrears/${editingId}`, form);
                toast.success('Arrears record updated successfully');
            } else {
                // ADD MODE — POST /payroll/arrears/calculate
                await axiosInstance.post('/payroll/arrears/calculate', form);
                toast.success('Arrears calculated and saved!');
            }
            closeModal();
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save arrears');
        } finally { setSaving(false); }
    };

    const approve = async (id) => {
        try {
            await axiosInstance.post(`/payroll/arrears/${id}/approve`);
            toast.success('Arrears approved — will appear in next payroll');
            load();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    };

    const reject = async (id) => {
        const reason = prompt('Rejection reason (required):');
        if (!reason?.trim()) return;
        try {
            await axiosInstance.post(`/payroll/arrears/${id}/reject`, { reason });
            toast.success('Arrears rejected');
            load();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    };

    // ── Derived validation flags (for isInvalid red borders) ──────────────
    const oldSalaryNum  = parseFloat(form.old_salary  || 0);
    const newSalaryNum  = parseFloat(form.new_salary  || 0);
    const oldValid      = form.old_salary !== '' && oldSalaryNum > 0;
    const newValid      = form.new_salary !== '' && newSalaryNum > 0;
    const newTooLow     = newValid && newSalaryNum <= oldSalaryNum;
    const yearNum       = parseInt(form.year);
    const yearInvalid   = !form.year || isNaN(yearNum) || yearNum < 2020 || yearNum > CY + 1;
    const notesLen      = (form.notes || '').length;

    return (
        <div>
            {/* ── Header ── */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h6 className="fw-bold mb-1 d-flex align-items-center gap-2">
                        <Calculator size={18} className="text-primary"/>
                        Salary Revision Arrears
                    </h6>
                    <p className="text-muted small mb-0">
                        Calculate arrears when an employee's salary is revised mid-month
                    </p>
                </div>
                <div className="d-flex gap-2">
                    <Form.Select size="sm" value={filterStatus}
                        onChange={e => setFilterStatus(e.target.value)}
                        style={{ width: 140 }}>
                        <option value="">All</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="paid">Paid</option>
                    </Form.Select>
                    <Button size="sm" variant="outline-secondary" onClick={load} title="Refresh">
                        <RefreshCw size={13}/>
                    </Button>
                    <Button size="sm" variant="primary" onClick={openAdd}>
                        <Plus size={13} className="me-1"/>Calculate Arrears
                    </Button>
                </div>
            </div>

            {/* ── List ── */}
            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" variant="primary"/>
                </div>
            ) : arrears.length === 0 ? (
                <Alert variant="info" className="text-center">
                    No arrears records found
                    {filterStatus ? ` with status "${filterStatus}"` : ''}.{' '}
                    {filterStatus
                        ? <Button variant="link" size="sm" className="p-0 align-baseline"
                            onClick={() => setFilterStatus('')}>Show all</Button>
                        : "Click Calculate Arrears when an employee's salary is revised."}
                </Alert>
            ) : (
                <Card className="border-0 shadow-sm">
                    <Table hover responsive className="mb-0 align-middle small">
                        <thead className="bg-light">
                            <tr className="text-uppercase text-muted" style={{ fontSize: 10 }}>
                                <th className="ps-4">Employee</th>
                                <th>Period</th>
                                <th>Old Salary</th>
                                <th>New Salary</th>
                                <th>Eff. Date</th>
                                <th>Days (New Rate)</th>
                                <th>Arrears</th>
                                <th>Status</th>
                                <th className="pe-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {arrears.map((a, i) => (
                                <tr key={a.id || i}>
                                    <td className="ps-4">
                                        <div className="fw-medium">{a.employee_name}</div>
                                        <div className="text-muted" style={{ fontSize: 10 }}>
                                            {a.employee_code}
                                        </div>
                                    </td>
                                    <td>{MONTH_NAMES[a.month]} {a.year}</td>
                                    <td className="text-muted">{fmt(a.old_salary)}</td>
                                    <td className="fw-medium">{fmt(a.new_salary)}</td>
                                    <td>{a.effective_date?.slice(0, 10)}</td>
                                    <td>
                                        <span title={`${a.days_at_old_salary}d old + ${a.days_at_new_salary}d new`}>
                                            {a.days_at_new_salary} days
                                        </span>
                                    </td>
                                    <td className="fw-bold text-success">{fmt(a.arrears_amount)}</td>
                                    <td><StatusBadge status={a.status}/></td>
                                    <td className="pe-4">
                                        <div className="d-flex gap-1">
                                            {a.status === 'pending' && (<>
                                                {/* ── EDIT button — pending only ── */}
                                                <Button size="sm" variant="outline-primary"
                                                    className="py-0 px-2" title="Edit this arrears record"
                                                    onClick={() => openEdit(a)}>
                                                    <Edit2 size={12}/>
                                                </Button>
                                                <Button size="sm" variant="outline-success"
                                                    className="py-0 px-2" title="Approve"
                                                    onClick={() => approve(a.id)}>
                                                    <CheckCircle size={12}/>
                                                </Button>
                                                <Button size="sm" variant="outline-danger"
                                                    className="py-0 px-2" title="Reject"
                                                    onClick={() => reject(a.id)}>
                                                    <XCircle size={12}/>
                                                </Button>
                                            </>)}
                                            {a.status !== 'pending' && (
                                                <span className="text-muted small">
                                                    {a.approved_by_name || '—'}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            )}

            {/* ── Add / Edit Modal ── */}
            <Modal show={showForm} onHide={closeModal} centered size="lg">
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fw-bold fs-6">
                        <Calculator size={16} className="me-2 text-primary"/>
                        {editingId ? 'Edit Salary Arrears' : 'Calculate Salary Arrears'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {editingId ? (
                        <Alert variant="warning" className="small py-2 mb-4">
                            ✏️ Editing a pending arrears record. Recalculate the preview after making changes,
                            then save. Only <strong>pending</strong> records can be edited.
                        </Alert>
                    ) : (
                        <Alert variant="info" className="small py-2 mb-4">
                            Use this when an employee's salary is revised mid-month.
                            Arrears = difference owed from the effective date to month-end.
                        </Alert>
                    )}

                    <Row className="g-3">
                        {/* Employee — locked in edit mode */}
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">
                                Employee *
                                {editingId && (
                                    <Badge bg="secondary" className="ms-2" style={{ fontSize: 9 }}>
                                        Locked in edit mode
                                    </Badge>
                                )}
                            </Form.Label>
                            <Form.Select
                                value={form.employee_id}
                                disabled={!!editingId}
                                isInvalid={attempted && !form.employee_id}
                                onChange={e => {
                                    const empId = e.target.value;
                                    setF('employee_id', empId);
                                    if (empId) {
                                        const emp = employees.find(
                                            em => String(em.id) === String(empId) ||
                                                  String(em.user_id) === String(empId)
                                        );
                                        if (emp) {
                                            const salary = emp.gross_salary || emp.monthly_salary ||
                                                           emp.basic_salary || '';
                                            if (salary) setF('old_salary', String(salary));
                                        }
                                    }
                                }}>
                                <option value="">
                                    {employees.length === 0
                                        ? '⚠ No employees loaded'
                                        : 'Select employee…'}
                                </option>
                                {employees.map(e => (
                                    <option key={e.id} value={e.id}>
                                        {e.first_name} {e.last_name}
                                        {e.employee_code ? ` (${e.employee_code})` : ''}
                                        {(e.gross_salary || e.basic_salary)
                                            ? ` — ₹${parseFloat(
                                                e.gross_salary || e.basic_salary
                                              ).toLocaleString('en-IN')}/mo`
                                            : ''}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">
                                Please select an employee
                            </Form.Control.Feedback>
                        </Col>

                        {/* Old Salary — ₹ prefix + isInvalid */}
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Old Monthly Salary *</Form.Label>
                            <InputGroup>
                                <InputGroup.Text>₹</InputGroup.Text>
                                <Form.Control
                                    type="number" placeholder="e.g. 95000" min="1"
                                    value={form.old_salary}
                                    onChange={e => setF('old_salary', e.target.value)}
                                    isInvalid={
                                        (attempted && (!form.old_salary || oldSalaryNum <= 0)) ||
                                        (form.old_salary !== '' && oldSalaryNum <= 0)
                                    }/>
                                <Form.Control.Feedback type="invalid">
                                    Must be greater than 0
                                </Form.Control.Feedback>
                            </InputGroup>
                            {oldValid && (
                                <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                                    Yearly: <strong>{fmtYearly(form.old_salary)}</strong> per annum
                                </div>
                            )}
                        </Col>

                        {/* New Salary — ₹ prefix + isInvalid */}
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">New Monthly Salary *</Form.Label>
                            <InputGroup>
                                <InputGroup.Text>₹</InputGroup.Text>
                                <Form.Control
                                    type="number" placeholder="e.g. 110000" min="1"
                                    value={form.new_salary}
                                    onChange={e => setF('new_salary', e.target.value)}
                                    isInvalid={
                                        (attempted && (!form.new_salary || newSalaryNum <= 0)) ||
                                        newTooLow
                                    }/>
                                <Form.Control.Feedback type="invalid">
                                    {newTooLow
                                        ? 'Must be higher than old salary'
                                        : 'Must be greater than 0'}
                                </Form.Control.Feedback>
                            </InputGroup>
                            {newValid && (
                                <div className="mt-1" style={{ fontSize: 11 }}>
                                    <span className="text-muted">
                                        Yearly: <strong>{fmtYearly(form.new_salary)}</strong> per annum
                                    </span>
                                    {newValid && oldValid && newSalaryNum > oldSalaryNum && (
                                        <span className="text-success ms-2">
                                            +{fmtYearly(newSalaryNum - oldSalaryNum)} increment
                                        </span>
                                    )}
                                    {newTooLow && (
                                        <span className="text-danger ms-2">
                                            ⚠ Must be higher than old salary
                                        </span>
                                    )}
                                </div>
                            )}
                        </Col>

                        {/* Effective Date — isInvalid when attempted + missing */}
                        <Col xs={4}>
                            <Form.Label className="small fw-bold">Effective Date *</Form.Label>
                            <Form.Control
                                type="date"
                                value={form.effective_date}
                                max={new Date().toISOString().slice(0, 10)}
                                onChange={e => setF('effective_date', e.target.value)}
                                isInvalid={attempted && !form.effective_date}/>
                            <Form.Control.Feedback type="invalid">
                                Effective date is required
                            </Form.Control.Feedback>
                        </Col>

                        {/* Month */}
                        <Col xs={4}>
                            <Form.Label className="small fw-bold">Month *</Form.Label>
                            <Form.Select value={form.month}
                                onChange={e => setF('month', parseInt(e.target.value))}>
                                {MONTH_NAMES.slice(1).map((m, i) => (
                                    <option key={i} value={i + 1}>{m}</option>
                                ))}
                            </Form.Select>
                        </Col>

                        {/* Year — isInvalid for out-of-range + NaN */}
                        <Col xs={4}>
                            <Form.Label className="small fw-bold">Year *</Form.Label>
                            <Form.Control
                                type="number"
                                min={2020} max={CY + 1}
                                value={form.year}
                                onChange={e => {
                                    const v = parseInt(e.target.value);
                                    setF('year', isNaN(v) ? CY : v);
                                }}
                                isInvalid={attempted && yearInvalid}/>
                            <Form.Control.Feedback type="invalid">
                                Year must be between 2020 and {CY + 1}
                            </Form.Control.Feedback>
                        </Col>

                        {/* Notes — maxLength 500 + character counter */}
                        <Col xs={12}>
                            <Form.Label className="small fw-bold d-flex justify-content-between">
                                <span>Notes</span>
                                <span className={`fw-normal ${notesLen > 450 ? 'text-danger' : 'text-muted'}`}
                                    style={{ fontSize: 10 }}>
                                    {notesLen}/500
                                </span>
                            </Form.Label>
                            <Form.Control
                                as="textarea" rows={2}
                                placeholder="e.g. Annual increment effective 15th May"
                                maxLength={500}
                                value={form.notes}
                                onChange={e => setF('notes', e.target.value)}
                                isInvalid={notesLen > 500}/>
                            <Form.Control.Feedback type="invalid">
                                Notes must be 500 characters or less
                            </Form.Control.Feedback>
                        </Col>

                        {/* Preview button */}
                        <Col xs={12}>
                            <Button variant="outline-primary" onClick={preview} className="w-100">
                                👁 Preview Calculation
                            </Button>
                        </Col>
                    </Row>

                    {/* ── Calculation Preview ── */}
                    {calcResult && (
                        <div className="mt-4 p-3 rounded-3"
                            style={{ background: '#f8faff', border: '1px solid #e0e7ff' }}>
                            <div className="fw-bold small mb-3 text-primary">📊 Arrears Breakdown</div>
                            <Row className="g-2 small">
                                {[
                                    { label: 'Days in Month',      value: calcResult.days_in_month },
                                    { label: 'Effective From Day', value: calcResult.effective_day },
                                    { label: 'Days at Old Rate',
                                      value: `${calcResult.days_at_old_salary} days${calcResult.effective_day > 1 ? ` (1st–${calcResult.effective_day-1}th)` : ''}` },
                                    { label: 'Days at New Rate',   value: `${calcResult.days_at_new_salary} days` },
                                    { label: 'Old Monthly Salary', value: fmt(form.old_salary) },
                                    { label: 'New Monthly Salary', value: fmt(form.new_salary) },
                                    { label: 'Old Yearly Salary',  value: fmtYearly(form.old_salary) },
                                    { label: 'New Yearly Salary',  value: fmtYearly(form.new_salary) },
                                    { label: 'Old Daily Rate',     value: `₹${parseFloat(calcResult.old_daily_rate).toFixed(2)}` },
                                    { label: 'New Daily Rate',     value: `₹${parseFloat(calcResult.new_daily_rate).toFixed(2)}` },
                                    { label: 'Paid at Old Rate',   value: fmt(calcResult.old_salary_portion) },
                                    { label: 'Paid at New Rate',   value: fmt(calcResult.new_salary_portion) },
                                ].map(({ label, value }, i) => (
                                    <Col xs={6} key={i}>
                                        <div className="d-flex justify-content-between">
                                            <span className="text-muted">{label}:</span>
                                            <span className="fw-medium">{value}</span>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                            <hr style={{ borderColor: '#e0e7ff' }}/>
                            <div className="d-flex justify-content-between fw-bold">
                                <span>Arrears Payable:</span>
                                <span className={`fs-6 ${calcResult.arrears_amount > 0 ? 'text-success' : 'text-danger'}`}>
                                    {fmt(calcResult.arrears_amount)}
                                </span>
                            </div>
                            {calcResult.arrears_amount <= 0 && (
                                <Alert variant="warning" className="mt-2 py-1 small mb-0">
                                    ⚠ Arrears = ₹0. If effective date is the 1st of the month,
                                    all days use the new rate — no arrears owed. The salary revision
                                    will still be recorded.
                                </Alert>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" onClick={closeModal}>Cancel</Button>
                    <Button variant="primary" onClick={save} disabled={saving || notesLen > 500}>
                        {saving
                            ? <><Spinner size="sm" className="me-1"/>Saving…</>
                            : editingId
                                ? '✏️ Update Arrears Record'
                                : '💾 Save Arrears Record'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PayrollArrears;
